import API from "../../../api/index";
import { useState, useEffect } from "react";
import { Box, Typography, Skeleton, Stack } from "@mui/material";
import { logColor, boxStyle } from "../../../theme";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezonePlugin from "dayjs/plugin/timezone";

import {
  ChartContainerPro,
  LinePlot,
  MarkPlot,
  ChartsXAxis,
  ChartsYAxis,
  ChartsTooltip,
  ChartsGrid,
  ChartsAxisHighlight,
} from "@mui/x-charts-pro";

dayjs.extend(utc);
dayjs.extend(timezonePlugin);

const isSfpType = (tipoLecs) =>
  tipoLecs === "LECS-CORE-SFP" || tipoLecs === "LECS-CORE-4-PRO";

const allSeriesConfig = [
  { id: "critical", label: "Critical", dataKey: "critical", color: logColor[1], type: "line", showMark: false, curve: "bumpX" },
  { id: "high", label: "High", dataKey: "high", color: logColor[0], type: "line", showMark: false, curve: "bumpX" },
  { id: "alert", label: "Alert", dataKey: "alert", color: logColor[2], type: "line", showMark: false, curve: "bumpX" },
  { id: "movements", label: "Movements", dataKey: "movements", color: logColor[3], type: "line", showMark: false, curve: "bumpX" },
  { id: "threat", label: "Threat Intelligence", dataKey: "threat", color: logColor[4], type: "line", showMark: false, curve: "bumpX" },
];

const GlobalAttack = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const tipoLecs = sessionStorage.getItem("tipoLecs");
  const timezone = sessionStorage.getItem("timezone");

  const series = isSfpType(tipoLecs)
    ? [allSeriesConfig[0], allSeriesConfig[1], allSeriesConfig[2], allSeriesConfig[4]]
    : [...allSeriesConfig];

  useEffect(() => {
    const controller = new AbortController()
    const sfp = isSfpType(tipoLecs);
    setLoading(true);
    API.get_home_graph({ sfp, timezone }, controller.signal).then((res) => {
      setData(res.data.response.map((item) => ({
        ...item,
        date: dayjs.utc(item.date).startOf("hour").valueOf(),
      })));
    }).catch((err) => {
      if (err.name !== 'CanceledError') setData([]);
    }).finally(() => setLoading(false));
    return () => controller.abort()
  }, [timezone, tipoLecs]);

  const activeSeries = loading
    ? series
    : series.filter((s) => data.some((d) => d[s.dataKey] != null));

  return (
    <Box sx={{ ...boxStyle }}>
      <ClickableMixedChart data={data} series={activeSeries} loading={loading} timezone={timezone} />
    </Box>
  );
};

export default GlobalAttack;

const ClickableMixedChart = ({ data, series, loading, timezone }) => {
  const navigate = useNavigate();
  const [visSeries, setVisSeries] = useState(series);

  useEffect(() => {
    setVisSeries(series);
  }, [series]);

  const handleClick = (_, params) => {
    if (!params) return;
    const clickedRow = data[params.dataIndex];
    if (!clickedRow) return;
    const clickedDate = dayjs(clickedRow.date).tz(timezone).utc().format("YYYY-MM-DD HH:00:00");
    let id = params.seriesId === "threat" ? "threat-intelligence" : params.seriesId;
    if (sessionStorage.getItem("type") === "user") {
      navigate(`/user/security/${id}?clickedData=${clickedDate}`);
    } else {
      navigate(`/reseller/myC/${id}?clickedData=${clickedDate}`);
    }
  };

  const handleToggleSeries = (seriesId) => {
    setVisSeries((prev) => {
      const exists = prev.some((s) => s.id === seriesId);
      if (exists) return prev.filter((s) => s.id !== seriesId);
      const original = series.find((s) => s.id === seriesId);
      return original ? [...prev, original] : prev;
    });
  };

  const Legend = ({ series: legendSeries, onItemClick }) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 1 }}>
      {legendSeries.map((item) => {
        const isVisible = visSeries.some((s) => s.id === item.id);
        return (
          <Box
            key={item.id}
            onClick={() => onItemClick(item.id)}
            sx={{
              display: "flex", alignItems: "center", gap: 0.8,
              cursor: "pointer", opacity: isVisible ? 1 : 0.35,
              userSelect: "none", transition: "opacity 0.2s ease",
              py: 0.5, px: 1, borderRadius: "6px",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.04)" },
            }}
          >
            <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: item.color, flexShrink: 0 }} />
            <Typography sx={{ fontSize: 12, fontFamily: "Poppins", color: "#ccc" }}>
              {item.label}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );

  if (loading) {
    return (
      <Box sx={{ width: "100%", p: 1 }}>
        <Stack direction="row" spacing={1} mb={1.5}>
          {[80, 60, 65, 70, 90].map((w, i) => (
            <Skeleton key={i} variant="rounded" width={w} height={18} sx={{ bgcolor: "rgba(255,255,255,0.08)" }} />
          ))}
        </Stack>
        <Skeleton variant="rounded" width="100%" height={220} sx={{ bgcolor: "rgba(255,255,255,0.06)", borderRadius: 1 }} />
      </Box>
    );
  }

  if (!series.length) {
    return (
      <Box sx={{ height: 250, width: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Typography sx={{ fontSize: 13 }}>No data available</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        "& .MuiChartsAxisHighlight-root line": { strokeDasharray: "5 3", stroke: "rgba(255, 255, 255, 0.25)", strokeWidth: 1 },
        "& .MuiChartsAxis-tickLabel": { fill: "#aaa !important" },
        "& .MuiChartsAxis-line": { stroke: "rgba(255,255,255,0.15) !important" },
        "& .MuiChartsAxis-tick": { stroke: "rgba(255,255,255,0.15) !important" },
      }}
    >
      <Legend series={series} onItemClick={handleToggleSeries} />

      <ChartContainerPro
        height={250}
        dataset={data}
        series={visSeries}
        margin={{ top: 10, right: 20, bottom: 30, left: 10 }}
        xAxis={[{ id: "x-axis", dataKey: "date", scaleType: "band", valueFormatter: (v) => dayjs(v).format("HH:mm") }]}
        yAxis={[{ id: "y-axis" }]}
      >
        <ChartsGrid horizontal />
        <LinePlot />
        <MarkPlot onItemClick={handleClick} />
        <ChartsAxisHighlight x="line" y="none" />
        <ChartsXAxis
          axisId="x-axis"
          tickLabelStyle={{ fill: "#aaa", fontSize: 11 }}
          sx={{ "& .MuiChartsAxis-tickLabel": { transformBox: "fill-box", transformOrigin: "right center", transform: "rotate(-45deg)" } }}
        />
        <ChartsYAxis axisId="y-axis" tickLabelStyle={{ fill: "#aaa", fontSize: 11 }} />
        <ChartsTooltip />
      </ChartContainerPro>
    </Box>
  );
};
